// Domain envelopes only. Provider validation schemas belong to the Sim integration.
export const VIDEO_BLUEPRINT_SYSTEM_FLAG = "video_blueprint_system";
export const VIDEO_WORKFLOWS = ["blueprint", "production"] as const;
export type VideoWorkflow = typeof VIDEO_WORKFLOWS[number];
export const BLUEPRINT_STAGES = [
  "created", "analyzing_reference", "building_spec", "resolving_assets", "generating_assets",
  "building_project", "rendering_preview", "agent_qa", "awaiting_human_review",
  "applying_revisions", "approved", "failed",
] as const;
export const PRODUCTION_STAGES = [
  "created", "planning_variants", "resolving_assets", "generating_assets",
  "cloning_master", "applying_variations", "rendering_previews", "agent_qa",
  "human_review", "auto_revision", "final_render", "complete", "failed",
] as const;
export type BlueprintStage = typeof BLUEPRINT_STAGES[number];
export type ProductionStage = typeof PRODUCTION_STAGES[number];
export type VideoStage = BlueprintStage | ProductionStage;
export type VideoJson = { [key: string]: unknown };
export type VideoAssetType = "video" | "voice" | "image" | "upscale";
export type VideoSlotType = "text" | "image" | "video" | "voice" | "offer" | "product" | "proof" | "cta";
export interface VideoAssetMetadata {
  duration_sec: number | null;
  width: number | null;
  height: number | null;
  fps: number | null;
  has_audio: boolean;
}
export interface VideoSlotSnapshot {
  slot_key: string;
  type: VideoSlotType;
  required: boolean;
  locked: boolean;
  constraints_json: VideoJson;
  allowed_variation_json: VideoJson;
}
export interface VideoApprovalSnapshot {
  creative_spec: VideoJson;
  resolved_assets: Record<string, { asset_id: string; uri: string }>;
  slots: VideoSlotSnapshot[];
  locked_elements: string[];
  project_id: string;
  project_version: number;
  project_uri: string;
  canonical_preview_uri: string;
}
export type VideoDispatchState = "pending" | "claimed" | "completed";
export type VideoAdmissionState = "not_started" | "submitting" | "submitted" | "needs_review";
export interface VideoDispatchProposal {
  operation: string;
  // Include scene/variant identity in operation for fan-out.
  payload: VideoJson;
}
export interface VideoContinuation {
  expected_stage: VideoStage;
  operation_key: string | null;
  data: VideoJson;
}
export interface VideoApprovedIdentity {
  brand_id: string;
  blueprint_id: string;
  version_id: string;
  version: number;
}
export const VIDEO_QA_ITEMS = [
  "matches_blueprint_structure", "brand_locks_respected", "captions_readable_in_safe_zone",
  "product_visuals_correct", "vo_aligned_with_scene_timing", "no_visual_artifacts_or_bad_text",
  "hook_executes_in_first_1_3s", "offer_proof_cta_factually_correct",
  "no_changes_outside_permitted_slots",
] as const;
export type VideoEditOp = "trim" | "reorder" | "replace_asset" | "update_text" |
  "update_caption_style" | "update_audio" | "set_timing";
export interface VideoQaDecision {
  decision: "approve" | "revise" | "human_review";
  reason: string;
  checklist: Array<{ item: typeof VIDEO_QA_ITEMS[number]; pass: boolean; note?: string }>;
  operations: Array<VideoJson & { op: VideoEditOp }>;
}
export function videoOperationKey(jobId: string, stage: VideoStage, attempt: number, operation: string): string {
  if (!jobId || !operation || !Number.isInteger(attempt) || attempt < 0) throw new Error("Invalid operation identity");
  return `${jobId}:${stage}:${attempt}:${operation}`;
}
